import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { useOperatorProfileStore } from '../stateManagers/stores/operatorProfileStore';
import apiClient from '../stateManagers/apiSetup/axiosInterceptor';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DashboardData {
  scope: { institute_id: string; branch_id: string };
  batches: { total: number; active: number; inactive: number };
  teachers: { total: number; active: number; registered: number };
  students: { total: number; active: number; enrolled: number; registered: number };
  courses: { total: number; active: number };
  recent_enrollments: {
    enrollment_id: string;
    enrollment_no: string;
    student_id: string;
    student_name: string;
    course_id: string;
    course_name: string;
    batch_id: string;
    batch_name: string;
    date_of_admission: string;
    enrolled_at: string;
  }[];
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy: '#0e225d',
  navyMid: '#1a3370',
  navyDark: '#0a1a3d',
  gold: '#fbb21f',
  goldDim: 'rgba(251,178,31,0.14)',
  goldBorder: 'rgba(251,178,31,0.3)',
  white: '#ffffff',
  border: '#e5e7eb',
  muted: '#9ca3af',
  sub: '#6b7280',
  body: '#1f2937',
  green: '#15803d',
  greenDim: 'rgba(22,163,74,0.1)',
  greenBorder: 'rgba(22,163,74,0.2)',
  red: '#dc2626',
  redDim: 'rgba(239,68,68,0.1)',
  redBorder: 'rgba(239,68,68,0.2)',
};

// ── StatCard ──────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  title: string;
  total: number;
  icon: string;
  accent?: boolean;
  badges?: { label: string; value: number; color: string }[];
}> = ({ title, total, icon, accent = false, badges = [] }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: accent
          ? `linear-gradient(145deg, ${C.navy} 0%, ${C.navyMid} 60%, ${C.navyDark} 100%)`
          : C.white,
        borderRadius: '14px',
        padding: '18px',
        border: accent ? 'none' : `1px solid ${C.border}`,
        boxShadow: hovered
          ? accent ? '0 12px 32px rgba(14,34,93,0.3)' : '0 8px 24px rgba(14,34,93,0.12)'
          : accent ? '0 2px 10px rgba(14,34,93,0.2)' : '0 1px 4px rgba(14,34,93,0.06)',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
      }}
    >
      {/* Decorative orb */}
      <div style={{
        position: 'absolute', top: '-30px', right: '-30px',
        width: '100px', height: '100px',
        background: accent ? 'rgba(251,178,31,0.1)' : 'rgba(14,34,93,0.04)',
        borderRadius: '50%', filter: 'blur(24px)', pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Icon */}
        <div style={{
          width: '36px', height: '36px',
          background: accent ? 'rgba(251,178,31,0.18)' : 'rgba(14,34,93,0.07)',
          border: accent ? '1px solid rgba(251,178,31,0.35)' : '1px solid rgba(14,34,93,0.1)',
          borderRadius: '9px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '12px',
        }}>
          <i className={icon} style={{
            fontSize: '0.9rem',
            color: accent ? C.gold : C.navy,
          }} />
        </div>

        <p style={{
          margin: '0 0 3px 0',
          fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: accent ? 'rgba(255,255,255,0.55)' : C.muted,
        }}>
          {title}
        </p>

        <p style={{
          margin: '0 0 12px 0',
          fontSize: '1.5rem', fontWeight: '800', lineHeight: 1,
          color: accent ? C.white : C.navy,
          letterSpacing: '-0.5px',
        }}>
          {total}
        </p>

        {/* Badges */}
        {badges.length > 0 && (
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {badges.map((b, i) => (
              <span key={i} style={{
                padding: '2px 7px',
                borderRadius: '20px',
                fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.2px',
                background: b.color === 'gold'
                  ? (accent ? C.gold : C.goldDim)
                  : b.color === 'green'
                  ? C.greenDim
                  : C.redDim,
                color: b.color === 'gold'
                  ? (accent ? C.navy : '#b8860b')
                  : b.color === 'green'
                  ? C.green
                  : C.red,
                border: b.color === 'gold'
                  ? (accent ? 'none' : `1px solid ${C.goldBorder}`)
                  : b.color === 'green'
                  ? `1px solid ${C.greenBorder}`
                  : `1px solid ${C.redBorder}`,
              }}>
                {b.value} {b.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { profile, loading: profileLoading } = useOperatorProfileStore();

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `.enrollment-row:hover { background: rgba(14,34,93,0.025) !important; }`;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (profile?.institute_id && profile?.branch_id) {
      fetchDashboard();
    }
  }, [profile]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<{ success: boolean; data: DashboardData }>('/v1/admin/dashboard');
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('Failed to load dashboard data.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  // ── Guard renders ─────────────────────────────────────────────────────────

  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '12px' }}>
        <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading profile…</p>
      </div>
    );
  }

  if (!profile?.institute_id || !profile?.branch_id) {
    return (
      <div style={{ padding: '20px' }}>
        <Message severity="warn" text="Institute or Branch information is missing. Please contact support." style={{ fontSize: '0.88rem' }} />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Message severity="error" text={error} style={{ fontSize: '0.88rem' }} />
      </div>
    );
  }

  if (!data) return null;

  const { batches, teachers, students, courses, recent_enrollments } = data;

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Toast ref={toast} />

      {/* ── Stat Cards Grid ──────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '14px',
      }}>
        <StatCard
          title="Total Batches"
          total={batches.total}
          icon="pi pi-th-large"
          accent
          badges={[
            { label: 'Active', value: batches.active, color: 'gold' },
            { label: 'Inactive', value: batches.inactive, color: 'red' },
          ]}
        />
        <StatCard
          title="Total Teachers"
          total={teachers.total}
          icon="pi pi-users"
          badges={[
            { label: 'Active', value: teachers.active, color: 'green' },
            { label: 'Registered', value: teachers.registered, color: 'gold' },
          ]}
        />
        <StatCard
          title="Total Students"
          total={students.total}
          icon="pi pi-user"
          badges={[
            { label: 'Enrolled', value: students.enrolled, color: 'green' },
            { label: 'Registered', value: students.registered, color: 'gold' },
          ]}
        />
      </div>

      {/* ── Quick Stats Bar ──────────────────────────────────────────────── */}
      <div style={{
        background: C.white,
        borderRadius: '14px',
        padding: '0',
        boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
        border: `1px solid ${C.border}`,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        overflow: 'hidden',
      }}>
        {[
          { label: 'Active Batches',  value: batches.active,  icon: 'pi pi-check-circle', color: C.green,   bg: C.greenDim },
          { label: 'Active Teachers', value: teachers.active, icon: 'pi pi-user-edit',    color: C.navy,    bg: 'rgba(14,34,93,0.07)' },
          { label: 'Active Students', value: students.active, icon: 'pi pi-user-plus',    color: '#b8860b', bg: C.goldDim },
        ].map((item, idx, arr) => (
          <div key={idx} style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '14px 18px',
            borderRight: idx < arr.length - 1 ? `1px solid ${C.border}` : 'none',
          }}>
            <div style={{
              width: '32px', height: '32px', flexShrink: 0,
              background: item.bg, borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className={item.icon} style={{ color: item.color, fontSize: '0.82rem' }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: '800', color: C.navy, lineHeight: 1, letterSpacing: '-0.3px' }}>
                {item.value}
              </p>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.7rem', color: C.muted, fontWeight: '600', letterSpacing: '0.3px' }}>
                {item.label}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Recent Enrollments ───────────────────────────────────────────── */}
      <div style={{
        background: C.white,
        borderRadius: '14px',
        boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
        border: `1px solid ${C.border}`,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 18px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '3px', height: '16px',
              background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)',
              borderRadius: '3px',
            }} />
            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: C.navy }}>
              Recent Enrollments
            </span>
          </div>
          <span style={{
            padding: '3px 10px',
            background: 'rgba(14,34,93,0.06)',
            borderRadius: '20px',
            fontSize: '0.7rem', fontWeight: '700', color: C.navy, letterSpacing: '0.3px',
          }}>
            {recent_enrollments.length} Latest
          </span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Student', 'Enrollment No', 'Course', 'Batch', 'Date of Admission'].map((h) => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: '0.7rem', fontWeight: '700', color: C.muted,
                    letterSpacing: '0.6px', textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    borderBottom: `2px solid ${C.border}`,
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent_enrollments.map((enr, idx) => (
                <tr
                  key={enr.enrollment_id}
                  className="enrollment-row"
                  style={{
                    borderTop: `1px solid #f3f4f6`,
                    transition: 'background 0.12s ease',
                    background: C.white,
                  }}
                >
                  {/* Student */}
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                      <div style={{
                        width: '28px', height: '28px', flexShrink: 0,
                        background: `hsl(${(idx * 67) % 360}, 55%, 88%)`,
                        borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.7rem', fontWeight: '700',
                        color: `hsl(${(idx * 67) % 360}, 55%, 35%)`,
                      }}>
                        {enr.student_name.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: '0.83rem', fontWeight: '500', color: C.body, whiteSpace: 'nowrap' }}>
                        {enr.student_name}
                      </span>
                    </div>
                  </td>

                  {/* Enrollment No */}
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      padding: '3px 9px',
                      background: 'rgba(14,34,93,0.06)',
                      borderRadius: '7px',
                      fontSize: '0.71rem', fontWeight: '500', color: C.navy,
                      letterSpacing: '0.2px',
                    }}>
                      {enr.enrollment_no}
                    </span>
                  </td>

                  {/* Course */}
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      padding: '3px 9px',
                      background: C.goldDim,
                      border: `1px solid ${C.goldBorder}`,
                      borderRadius: '7px',
                      fontSize: '0.71rem', fontWeight: '500', color: '#b8860b',
                    }}>
                      {enr.course_name}
                    </span>
                  </td>

                  {/* Batch */}
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ fontSize: '0.83rem', fontWeight: '400', color: C.sub, whiteSpace: 'nowrap' }}>
                      {enr.batch_name}
                    </span>
                  </td>

                  {/* Date */}
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <i className="pi pi-calendar" style={{ color: C.navy, fontSize: '0.75rem', opacity: 0.4 }} />
                      <span style={{ fontSize: '0.83rem', fontWeight: '400', color: C.sub }}>
                        {enr.date_of_admission}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {recent_enrollments.length === 0 && (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <i className="pi pi-inbox" style={{ fontSize: '2rem', color: C.navy, opacity: 0.2 }} />
              <p style={{ marginTop: '10px', color: C.muted, fontSize: '0.88rem' }}>No recent enrollments</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;